import { useEffect } from "react";
import { ShieldCheck, Clock, Users, ClipboardCheck, Home, HeartHandshake } from "lucide-react";
import { Reveal, Stagger, StaggerItem } from "../components/Reveal";
import { Overline, PageHero, PrimaryLink, StatBlock } from "../components/Bits";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "../components/ui/accordion";
import { IMAGES } from "../lib/site";

const OFFERS = [
  { icon: Home, t: "Quality housing stock", d: "Safe, well-maintained, compliant accommodation ready for placement across the North West." },
  { icon: Clock, t: "Rapid placement", d: "Fast, responsible tenant placement to help you meet demand and reduce temporary spend." },
  { icon: ShieldCheck, t: "Compliance-first", d: "CQC & HMO standards, Awaab's-Law-ready, with full documentation and audit trails." },
  { icon: HeartHandshake, t: "24/7 support", d: "Round-the-clock tenant support and clear care pathways for vulnerable residents." },
  { icon: ClipboardCheck, t: "Inspection-backed", d: "Transparent, evidence-based property condition reporting on every home." },
  { icon: Users, t: "True partnership", d: "A single accountable point of contact and close collaboration with your teams." },
];

const FAQS = [
  {
    q: "What supported housing do you provide?",
    a: "Safe, compliant accommodation for vulnerable and at-risk individuals across the North West, paired with 24/7 tenant support and clear, person-centred care pathways.",
  },
  {
    q: "How quickly can you place tenants?",
    a: "We offer rapid, responsible placement into quality accommodation to help you meet demand and reduce reliance on costly temporary housing.",
  },
  {
    q: "Are your properties compliant?",
    a: "Yes. All accommodation meets CQC and HMO standards, is Awaab's-Law-ready, and comes with full documentation, inspections and audit trails.",
  },
  {
    q: "What support do tenants receive?",
    a: "Residents receive round-the-clock support and clear care pathways tailored to their individual needs, helping them sustain their tenancies.",
  },
  {
    q: "How do you work with councils and housing associations?",
    a: "As a genuine partner — you get a single accountable point of contact, close collaboration with your teams, and transparent, evidence-based reporting.",
  },
  {
    q: "Which areas do you cover?",
    a: "We operate across the North West of England, including Manchester, Liverpool, Salford, Bolton, Preston, Blackburn, Wigan and the surrounding towns.",
  },
  {
    q: "How do we start a partnership?",
    a: "Get in touch with our partnerships team and we'll discuss your placement needs, our standards and current availability across the region.",
  },
];

export default function LocalAuthorities() {
  useEffect(() => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "faq-jsonld-la";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQS.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });
    document.head.appendChild(script);
    return () => {
      const ex = document.getElementById("faq-jsonld-la");
      if (ex) ex.remove();
    };
  }, []);

  return (
    <div data-testid="local-authorities-page">
      <PageHero
        overline="For local authorities"
        titleLines={["Quality housing.", "Real partnership."]}
        subtitle="We help councils and housing associations place vulnerable people quickly into safe, compliant, supported accommodation — backed by 24/7 support and inspection-grade standards."
        image={IMAGES.skylineDay}
      />

      <section className="container-x -mt-6 relative z-10">
        <Stagger className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
          {[
            { v: "24/7", l: "Tenant support" },
            { v: "100%", l: "Compliance focus" },
            { v: "12+", l: "Towns covered" },
            { v: "1", l: "Point of contact" },
          ].map((s, i) => (
            <StaggerItem key={i}><StatBlock value={s.v} label={s.l} testid={`la-stat-${i}`} /></StaggerItem>
          ))}
        </Stagger>
      </section>

      <section className="container-x py-24 lg:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <Reveal><Overline>Our partnership model</Overline></Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-5 font-display font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-white">
                A dependable partner for supported housing.
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-6 text-slate-300 leading-relaxed">
                We understand the pressures on local authorities — rising demand, tight budgets and
                the duty to keep people safe. Insight Properties gives you quality accommodation and
                genuine support, wrapped in the compliance and transparency you need.
              </p>
            </Reveal>
            <Reveal delay={0.3}>
              <div className="mt-8"><PrimaryLink to="/contact" testid="la-cta-top">Partner with us</PrimaryLink></div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Stagger className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {OFFERS.map((o, i) => (
                <StaggerItem key={i}>
                  <div className="card-surface h-full rounded-3xl p-7 hover:border-amber/30 transition-colors" data-testid={`la-offer-${i}`}>
                    <o.icon className="h-7 w-7 text-amber" />
                    <h3 className="mt-4 font-display font-bold text-lg text-white">{o.t}</h3>
                    <p className="mt-2 text-sm text-slate-400 leading-relaxed">{o.d}</p>
                  </div>
                </StaggerItem>
              ))}
            </Stagger>
          </div>
        </div>
      </section>

      {/* COUNCIL FAQ */}
      <section className="container-x pb-4 lg:pb-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-4">
            <Reveal><Overline>Council FAQ</Overline></Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-5 font-display font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-white">
                Common questions.
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-6 text-slate-300 leading-relaxed max-w-sm">
                What councils, housing associations and support providers ask most. Need more detail?{" "}
                <a href="mailto:contact@insightproperties.co.uk" className="text-amber hover:text-amber-soft transition-colors">
                  Email our partnerships team.
                </a>
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-8">
            <Reveal>
              <Accordion type="single" collapsible className="w-full" data-testid="council-faq">
                {FAQS.map((f, i) => (
                  <AccordionItem key={i} value={`la-faq-${i}`} className="border-white/10">
                    <AccordionTrigger
                      data-testid={`la-faq-trigger-${i}`}
                      className="text-base sm:text-lg font-display font-semibold text-white hover:no-underline py-5"
                    >
                      {f.q}
                    </AccordionTrigger>
                    <AccordionContent data-testid={`la-faq-content-${i}`} className="text-slate-400 text-base leading-relaxed">
                      {f.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden">
        <div className="container-x pb-28 lg:pb-40 pt-16 lg:pt-24">
          <Reveal>
            <div className="relative overflow-hidden rounded-[2rem] min-h-[24rem] flex items-center">
              <img src={IMAGES.salfordQuays} alt="North West housing" className="absolute inset-0 h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/85 to-ink/40" />
              <div className="relative p-10 lg:p-16 max-w-xl">
                <h2 className="font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-white leading-[0.95]">
                  Let's solve housing demand together.
                </h2>
                <p className="mt-5 text-slate-300">
                  Speak to our partnerships team about placements, standards and availability across
                  the North West.
                </p>
                <div className="mt-8"><PrimaryLink to="/contact" testid="la-cta">Contact partnerships</PrimaryLink></div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
